import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type ModalKind = 'contact' | 'course' | 'suggestion';

interface ModalsContextValue {
  openModal: ModalKind | null;
  open: (kind: ModalKind) => void;
  close: () => void;
}

const ModalsContext = createContext<ModalsContextValue | null>(null);

export function ModalsProvider({ children }: { children: ReactNode }) {
  const [openModal, setOpenModal] = useState<ModalKind | null>(null);

  const open = useCallback((kind: ModalKind) => setOpenModal(kind), []);
  const close = useCallback(() => setOpenModal(null), []);

  const value = useMemo(
    () => ({ openModal, open, close }),
    [openModal, open, close],
  );

  return (
    <ModalsContext.Provider value={value}>{children}</ModalsContext.Provider>
  );
}

export function useModals() {
  const context = useContext(ModalsContext);
  if (!context) {
    throw new Error('useModals must be used within a ModalsProvider');
  }
  return context;
}
